'use client';
import * as React from 'react';
import { useIsoLayoutEffect } from '@base-ui/utils/useIsoLayoutEffect';
import { useFieldRootContext } from '../field-root-context/FieldRootContext';
import type { FieldControlRegistration } from './useFieldControlRegistration';

export function useRegisterFieldControl(
  controlRef: FieldControlRegistration['controlRef'],
  id: FieldControlRegistration['id'],
  value: FieldControlRegistration['value'],
  name?: FieldControlRegistration['name'] | undefined,
  options?: UseRegisterFieldControlOptions | undefined,
) {
  const enabled = options?.enabled ?? true;
  const getValue = options?.getValue;
  const isDirty = options?.isDirty;
  const resetValue = options?.resetValue;

  const { registerFieldControl } = useFieldRootContext();
  const sourceRef = React.useRef<symbol | null>(null);

  if (!sourceRef.current) {
    sourceRef.current = Symbol();
  }

  useIsoLayoutEffect(() => {
    const source = sourceRef.current;

    if (!source || !enabled) {
      return undefined;
    }

    const registration: FieldControlRegistration = {
      controlRef,
      getValue,
      id,
      isDirty,
      name,
      resetValue,
      value,
    };

    registerFieldControl(source, registration);

    return () => {
      registerFieldControl(source, undefined);
    };
  }, [controlRef, enabled, getValue, id, isDirty, name, registerFieldControl, resetValue, value]);
}

export interface UseRegisterFieldControlOptions {
  getValue?: FieldControlRegistration['getValue'] | undefined;
  enabled?: boolean | undefined;
  isDirty?: FieldControlRegistration['isDirty'] | undefined;
  resetValue?: FieldControlRegistration['resetValue'] | undefined;
}
